import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { PrimaryButton, SecondaryButton } from 'shared/components/button'
import Modal from 'shared/components/modal'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { UnprocessableContentError } from 'shared/errors/unprocessable-content-error'

type SubmitAssignmentModalProps = {
  onCloseModal: () => void
  onConfirm: () => void
  title?: string
  confirmationContent?: React.ReactNode
  errorMessageMap?: Partial<Record<'unprocessableContentError' | 'internalError', string>>
}

export const SubmitAssignmentModal = ({
  onCloseModal,
  onConfirm,
  title = 'confirmation_modal.default_title',
  confirmationContent = '',
  errorMessageMap,
}: SubmitAssignmentModalProps) => {
  const { t } = useTranslation()
  const [opened, setOpened] = useState(true)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  function closeModal() {
    setOpened(false)
    onCloseModal()
  }

  const handleConfirm = async () => {
    try {
      setIsLoading(true)
      await onConfirm()
      closeModal()
    } catch (e) {
      if (e instanceof BadRequest && e.errors.common) {
        setError(e.errors.common.join(''))
      } else if (e instanceof UnprocessableContentError) {
        setError(t(errorMessageMap?.['unprocessableContentError'] || ''))
      } else if (e instanceof InternalError) {
        setError(t(errorMessageMap?.['internalError'] || 'core.error.internal_error_message'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal opened={opened} onClose={closeModal} title={t(title)} small>
        <div className="text-THEME-primary/60 mb-6 flex justify-center">{confirmationContent}</div>
        <div className="mb-6 flex justify-center gap-4">
          <SecondaryButton className="flex-1 px-7" onClick={onCloseModal}>
            {t('confirmation_modal.cancel')}
          </SecondaryButton>
          <PrimaryButton
            className="ml-5 flex-1 p-1 px-7"
            onClick={handleConfirm}
            isLoading={isLoading}
          >
            {t('confirmation_modal.confirm')}
          </PrimaryButton>
        </div>
        {error && <p className="mt-2 text-center text-sm text-red">{error}</p>}
      </Modal>
    </>
  )
}
